'use client';

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Filter,
  Users,
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  UserPlus,
  RefreshCw,
  ChevronDown,
  MoreVertical,
  Mail,
  Phone,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  Ban,
  Play,
  History,
  Info,
} from 'lucide-react';
import {
  messagingApi,
  DMChannelFilters,
  DMChannelSummary,
  DMChannelListResponse,
  CaseManagerWorkload,
  DMChannelStats,
  EngagementState,
  DMSortBy,
  AssignChannelRequest,
} from '@/services/messagingApi';
import { userManagementAPI } from '@/services/api/userManagement';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { cn } from '@/lib/utils';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Engagement state configuration
const ENGAGEMENT_STATE_CONFIG: Record<EngagementState, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  new: { label: 'New', color: 'text-blue-600', bgColor: 'bg-blue-100', icon: <UserPlus className="h-3 w-3" /> },
  outreach: { label: 'Outreach', color: 'text-purple-600', bgColor: 'bg-purple-100', icon: <MessageSquare className="h-3 w-3" /> },
  engaged: { label: 'Engaged', color: 'text-green-600', bgColor: 'bg-green-100', icon: <CheckCircle2 className="h-3 w-3" /> },
  active: { label: 'Active', color: 'text-emerald-600', bgColor: 'bg-emerald-100', icon: <Activity className="h-3 w-3" /> },
  stale: { label: 'Stale', color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: <Clock className="h-3 w-3" /> },
  inactive: { label: 'Inactive', color: 'text-orange-600', bgColor: 'bg-orange-100', icon: <Minus className="h-3 w-3" /> },
  no_response: { label: 'No Response', color: 'text-red-600', bgColor: 'bg-red-100', icon: <XCircle className="h-3 w-3" /> },
  dormant: { label: 'Dormant', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: <Ban className="h-3 w-3" /> },
  opt_out: { label: 'Opted Out', color: 'text-slate-600', bgColor: 'bg-slate-100', icon: <Ban className="h-3 w-3" /> },
};

// Filter presets
const FILTER_PRESETS = {
  all: {
    label: 'All Channels',
    filters: { my_assignments: false },
  },
  myActive: {
    label: 'My Active',
    filters: { my_assignments: true, engagement_states: ['active', 'engaged'] as EngagementState[] },
  },
  needsAttention: {
    label: 'Needs Attention',
    filters: { my_assignments: true, awaiting_response: true },
  },
  followUp: {
    label: 'Follow-up Required',
    filters: { my_assignments: true, needs_followup: true },
  },
  unassigned: {
    label: 'Unassigned',
    filters: { unassigned_only: true, engagement_states: ['new', 'outreach', 'engaged'] as EngagementState[] },
  },
  staleInactive: {
    label: 'Stale/Inactive',
    filters: { engagement_states: ['stale', 'inactive', 'dormant'] as EngagementState[] },
  },
};

interface DMChannelManagementProps {
  /** `memberId` is the conversation's member, when known (TRUS-745: Org Settings opens it in the Inbox). */
  onSelectChannel?: (channelId: number, memberId?: number | null) => void;
}

export function DMChannelManagement({ onSelectChannel }: DMChannelManagementProps) {
  const queryClient = useQueryClient();
  const { fmt } = useFormattedDate();
  const [filters, setFilters] = useState<DMChannelFilters>({
    my_assignments: false,  // Show all DM channels by default
    sort_by: 'engagement_score',
    sort_order: 'desc',
    limit: 50,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCaseManager, setSelectedCaseManager] = useState<number | null>(null);
  const [assignReason, setAssignReason] = useState('');
  const [bulkReassignDialogOpen, setBulkReassignDialogOpen] = useState(false);
  const [bulkFromUser, setBulkFromUser] = useState<number | null>(null);
  const [bulkToUser, setBulkToUser] = useState<number | null>(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkIncludeDM, setBulkIncludeDM] = useState(true);
  const [bulkIncludeMembers, setBulkIncludeMembers] = useState(true);
  const [bulkIncludeEnrollments, setBulkIncludeEnrollments] = useState(true);
  // View controls: collapse duplicate same-member rows, hide empty channels, optional grouping.
  const [hideEmpty, setHideEmpty] = useState(true);
  const [groupBy, setGroupBy] = useState<'none' | 'engagement_state' | 'assigned'>('none');
  // Always-visible State/Score guidance (dismissible, persisted). The header info
  // icons are hover-only, so this panel makes the explanation visible on the page.
  const [showGuide, setShowGuide] = useState<boolean>(() => {
    try { return localStorage.getItem('dm_mgmt_guide_dismissed') !== '1'; } catch { return true; }
  });
  const dismissGuide = () => {
    setShowGuide(false);
    try { localStorage.setItem('dm_mgmt_guide_dismissed', '1'); } catch { /* ignore */ }
  };

  // Fetch DM channels
  const { data: channelData, isLoading: loadingChannels, refetch: refetchChannels } = useQuery({
    queryKey: ['dm-channels', filters],
    queryFn: () => messagingApi.listDMChannels({ ...filters, search: searchTerm || undefined }),
    refetchInterval: 60000, // Refresh every minute
  });

  // Collapse to one row per member (a member can have >1 DM channel from historical
  // dupes), optionally drop empty channels, and optionally group into sections. This
  // is what kills the confusing "multiple lines, same name" problem.
  const sections = useMemo(() => {
    const all = channelData?.channels ?? [];
    const filtered = hideEmpty ? all.filter((c) => (c.total_message_count ?? 0) > 0) : all.slice();
    const byMember = new Map<string, DMChannelSummary & { dupCount: number }>();
    for (const c of filtered) {
      const key = c.member_id != null ? `m-${c.member_id}` : `c-${c.id}`;
      const prev = byMember.get(key);
      if (!prev) {
        byMember.set(key, { ...c, dupCount: 0 });
      } else {
        const cBetter =
          (c.total_message_count ?? 0) > (prev.total_message_count ?? 0) ||
          ((c.total_message_count ?? 0) === (prev.total_message_count ?? 0) &&
            (c.last_message_at ?? '') > (prev.last_message_at ?? ''));
        const winner = cBetter ? { ...c } : { ...prev };
        byMember.set(key, { ...winner, dupCount: prev.dupCount + 1 });
      }
    }
    const rows = Array.from(byMember.values());
    if (groupBy === 'none') return [{ key: 'all', label: '', rows }];
    const groups = new Map<string, typeof rows>();
    for (const r of rows) {
      const gk =
        groupBy === 'engagement_state'
          ? r.engagement_state || 'new'
          : r.assigned_case_manager_name || '__unassigned__';
      if (!groups.has(gk)) groups.set(gk, []);
      groups.get(gk)!.push(r);
    }
    return Array.from(groups.entries()).map(([key, gRows]) => ({
      key,
      label:
        groupBy === 'engagement_state'
          ? ENGAGEMENT_STATE_CONFIG[key as EngagementState]?.label || key
          : key === '__unassigned__'
            ? 'Unassigned'
            : key,
      rows: gRows,
    }));
  }, [channelData, hideEmpty, groupBy]);
  const visibleCount = sections.reduce((n, s) => n + s.rows.length, 0);

  // Fetch workload summary (for display)
  const { data: workloadData } = useQuery({
    queryKey: ['case-manager-workload'],
    queryFn: () => messagingApi.getCaseManagerWorkload(),
    refetchInterval: 120000,
  });

  // Fetch available staff for assignment dialog
  const { data: availableStaff } = useQuery({
    queryKey: ['available-staff'],
    queryFn: () => messagingApi.getAvailableStaff(),
    enabled: assignDialogOpen, // Only fetch when dialog is open
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['dm-channel-stats'],
    queryFn: () => messagingApi.getDMChannelStats(),
    refetchInterval: 120000,
  });

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: (request: AssignChannelRequest) => messagingApi.assignChannels(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
      queryClient.invalidateQueries({ queryKey: ['case-manager-workload'] });
      queryClient.invalidateQueries({ queryKey: ['available-staff'] });
      setSelectedChannels([]);
      setAssignDialogOpen(false);
    },
  });

  // Opt-out mutation
  const optOutMutation = useMutation({
    mutationFn: (channelId: number) => messagingApi.markChannelOptOut(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channel-stats'] });
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: (channelId: number) => messagingApi.reactivateChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channel-stats'] });
    },
  });

  // Set engagement state mutation
  const setStateMutation = useMutation({
    mutationFn: ({ channelId, state }: { channelId: number; state: EngagementState }) =>
      messagingApi.setEngagementState(channelId, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channel-stats'] });
    },
  });

  // Bulk reassign mutation
  const bulkReassignMutation = useMutation({
    mutationFn: (request: {
      from_user_id: number;
      to_user_id: number;
      reason?: string;
      include_dm_channels?: boolean;
      include_member_assignments?: boolean;
    }) => userManagementAPI.bulkReassignCases(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dm-channels'] });
      queryClient.invalidateQueries({ queryKey: ['case-manager-workload'] });
      queryClient.invalidateQueries({ queryKey: ['available-staff'] });
      queryClient.invalidateQueries({ queryKey: ['dm-channel-stats'] });
      setBulkReassignDialogOpen(false);
      setBulkFromUser(null);
      setBulkToUser(null);
      setBulkReason('');
    },
  });

  const handleBulkReassign = () => {
    if (!bulkFromUser || !bulkToUser || bulkFromUser === bulkToUser) return;
    bulkReassignMutation.mutate({
      from_user_id: bulkFromUser,
      to_user_id: bulkToUser,
      reason: bulkReason || undefined,
      include_dm_channels: bulkIncludeDM,
      include_member_assignments: bulkIncludeMembers,
      include_enrollments: bulkIncludeEnrollments,
    });
  };

  const handlePresetClick = (presetKey: keyof typeof FILTER_PRESETS) => {
    const preset = FILTER_PRESETS[presetKey];
    setFilters({
      ...preset.filters,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      limit: filters.limit,
    });
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchTerm || undefined }));
  };

  const handleSelectAll = () => {
    if (selectedChannels.length === channelData?.channels.length) {
      setSelectedChannels([]);
    } else {
      setSelectedChannels(channelData?.channels.map(c => c.id) || []);
    }
  };

  const handleAssign = () => {
    if (!selectedCaseManager || selectedChannels.length === 0) return;
    assignMutation.mutate({
      channel_ids: selectedChannels,
      assign_to_user_id: selectedCaseManager,
      assignment_type: 'reassignment',
      reason: assignReason || undefined,
    });
  };

  const getEngagementScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEngagementTrend = (channel: DMChannelSummary) => {
    if (channel.days_inactive <= 7) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (channel.days_inactive <= 30) return <Minus className="h-4 w-4 text-yellow-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 gap-3 p-3 border-b sm:grid-cols-3 sm:gap-4 sm:p-4 lg:grid-cols-5">
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Total Channels</div>
          <div className="text-2xl font-bold">{statsData?.total_channels || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Active</div>
          <div className="text-2xl font-bold text-green-600">
            {(statsData?.by_state?.active || 0) + (statsData?.by_state?.engaged || 0)}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Awaiting Response</div>
          <div className="text-2xl font-bold text-yellow-600">{statsData?.awaiting_response || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Needs Follow-up</div>
          <div className="text-2xl font-bold text-orange-600">{statsData?.needs_followup || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-muted-foreground">Avg Score</div>
          <div className={cn("text-2xl font-bold", getEngagementScoreColor(statsData?.avg_engagement_score || 0))}>
            {statsData?.avg_engagement_score || 0}
          </div>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="p-4 border-b space-y-3">
        {/* Preset Filters */}
        <div className="flex gap-2 flex-wrap">
          {Object.entries(FILTER_PRESETS).map(([key, preset]) => (
            <Button
              key={key}
              variant={JSON.stringify(filters) === JSON.stringify({ ...preset.filters, sort_by: filters.sort_by, sort_order: filters.sort_order, limit: filters.limit }) ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetClick(key as keyof typeof FILTER_PRESETS)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        {/* Search and Advanced Filters */}
        <div className="flex gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by member name, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-9"
            />
          </div>

          <Select
            value={filters.sort_by}
            onValueChange={(value) => setFilters(prev => ({ ...prev, sort_by: value as DMSortBy }))}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="engagement_score">Engagement Score</SelectItem>
              <SelectItem value="last_activity">Last Activity</SelectItem>
              <SelectItem value="unread_count">Unread Count</SelectItem>
              <SelectItem value="member_name">Member Name</SelectItem>
            </SelectContent>
          </Select>

          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as typeof groupBy)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No grouping</SelectItem>
              <SelectItem value="engagement_state">Group: Engagement</SelectItem>
              <SelectItem value="assigned">Group: Assigned to</SelectItem>
            </SelectContent>
          </Select>

          <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-muted-foreground">
            <Checkbox checked={hideEmpty} onCheckedChange={(c) => setHideEmpty(!!c)} />
            Hide empty
          </label>

          <Button variant="outline" size="icon" onClick={() => refetchChannels()}>
            <RefreshCw className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkReassignDialogOpen(true)}
            className="gap-1"
          >
            <Users className="h-4 w-4" />
            Bulk Reassign Staff
          </Button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedChannels.length > 0 && (
        <div className="p-3 bg-blue-50 border-b flex items-center gap-3">
          <span className="text-sm font-medium">{selectedChannels.length} selected</span>
          <Button size="sm" onClick={() => setAssignDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-1" /> Reassign
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSelectedChannels([])}>
            Clear Selection
          </Button>
        </div>
      )}

      {/* Visible State/Score guidance (dismissible) */}
      {showGuide && (
        <div className="mx-3 mb-2 mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
            <div className="space-y-1">
              <div>
                <span className="font-semibold text-slate-700 dark:text-slate-200">State</span> — set automatically from recent activity:{' '}
                <b>Active</b> (member replied &lt;14d) · <b>Engaged</b> (replied, 14–30d) · <b>Outreach</b> (no reply yet) ·{' '}
                <b>Stale</b> (14–30d quiet) · <b>Inactive / No&nbsp;response</b> (30–90d) · <b>Dormant</b> (90d+) · <b>New</b> (no messages)
              </div>
              <div>
                <span className="font-semibold text-slate-700 dark:text-slate-200">Score</span> — 0–100 conversation health, recalculated on every message: last-message recency + member response rate + two-way balance + conversation depth + recent reply
              </div>
            </div>
            <button
              onClick={dismissGuide}
              className="ml-auto flex-shrink-0 rounded px-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700"
              aria-label="Dismiss guidance"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Channel List */}
      <div className="flex-1 overflow-auto">
        {loadingChannels ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : channelData?.channels.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <MessageSquare className="h-12 w-12 mb-2" />
            <p>No DM channels match your filters</p>
          </div>
        ) : (
          <table className="w-full min-w-[900px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-sm text-muted-foreground">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={selectedChannels.length === channelData?.channels.length}
                    onCheckedChange={handleSelectAll}
                  />
                </th>
                <th className="p-3">Member</th>
                <th className="p-3">Topic / Latest</th>
                <th className="p-3">Assigned To</th>
                <th className="p-3">
                  <span className="inline-flex items-center gap-1">
                    State
                    <Info
                      className="h-3.5 w-3.5 text-slate-400 cursor-help"
                      aria-label="State help"
                      title={
                        "Conversation state — set automatically from recent activity:\n" +
                        "• Active — member replied, last reply within 14 days (live two-way)\n" +
                        "• Engaged — member replied, but last reply 14–30 days ago\n" +
                        "• Outreach — staff messaged, member hasn't replied yet\n" +
                        "• Stale — no activity 14–30 days\n" +
                        "• Inactive / No response — 30–90 days (No response = member never replied)\n" +
                        "• Dormant — no activity 90+ days\n" +
                        "• New — no messages yet · Opted out — member opted out"
                      }
                    />
                  </span>
                </th>
                <th className="p-3 text-center">
                  <span className="inline-flex items-center gap-1">
                    Score
                    <Info
                      className="h-3.5 w-3.5 text-slate-400 cursor-help"
                      aria-label="Score help"
                      title={
                        "Conversation-health score (0–100), recalculated on every message:\n" +
                        "• Last-message recency (0–30)\n" +
                        "• Member response rate (0–25)\n" +
                        "• Two-way balance — member's share of messages, best 40–60% (0–20)\n" +
                        "• Conversation depth — total messages (0–15)\n" +
                        "• Recent reply bonus (0–10)"
                      }
                    />
                  </span>
                </th>
                <th className="p-3">Last Activity</th>
                <th className="p-3 text-center">Messages</th>
                <th className="p-3 text-center">Flags</th>
                <th className="p-3 w-12 text-center sticky right-0 z-20 bg-slate-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <React.Fragment key={`sec-${section.key}`}>
                {groupBy !== 'none' && (
                  <tr className="bg-slate-100/80 border-b">
                    <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      {section.label}<span className="ml-1 font-normal text-slate-400">· {section.rows.length}</span>
                    </td>
                  </tr>
                )}
                {section.rows.map((channel) => {
                const stateConfig = ENGAGEMENT_STATE_CONFIG[channel.engagement_state];
                return (
                  <tr
                    key={channel.id}
                    className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => onSelectChannel?.(channel.id, channel.member_id)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedChannels.includes(channel.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedChannels([...selectedChannels, channel.id]);
                          } else {
                            setSelectedChannels(selectedChannels.filter(id => id !== channel.id));
                          }
                        }}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-medium flex items-center gap-1.5">
                        {channel.member_name}
                        {channel.dupCount > 0 && (
                          <span
                            className="rounded bg-slate-200 px-1 text-[10px] font-semibold text-slate-600"
                            title={`${channel.dupCount} additional channel(s) for this member collapsed into this row`}
                          >
                            +{channel.dupCount}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        {channel.member_email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {channel.member_email}
                          </span>
                        )}
                        {channel.member_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {channel.member_phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 max-w-[260px]">
                      {channel.topic || channel.last_message_preview ? (
                        <div className="truncate text-sm" title={channel.topic || channel.last_message_preview || ''}>
                          {channel.topic && <span className="font-medium">{channel.topic}</span>}
                          {channel.topic && channel.last_message_preview ? ' · ' : ''}
                          {channel.last_message_preview && (
                            <span className="text-muted-foreground">{channel.last_message_preview}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      {channel.assigned_case_manager_name ? (
                        <span className="text-sm">{channel.assigned_case_manager_name}</span>
                      ) : (
                        <Badge variant="outline" className="text-orange-600">Unassigned</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={cn("text-xs", stateConfig.bgColor, stateConfig.color)}>
                        {stateConfig.icon}
                        <span className="ml-1">{stateConfig.label}</span>
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={cn("font-bold", getEngagementScoreColor(channel.engagement_score))}>
                          {channel.engagement_score}
                        </span>
                        {getEngagementTrend(channel)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        {channel.last_message_at
                          ? fmt.short(channel.last_message_at)
                          : 'Never'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {channel.days_inactive} days ago
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <div className="text-sm">{channel.total_message_count}</div>
                      <div className="text-xs text-muted-foreground">
                        {channel.member_message_count} / {channel.staff_message_count}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        {channel.unread_count > 0 && (
                          <Badge variant="destructive" className="text-xs">{channel.unread_count}</Badge>
                        )}
                        {channel.awaiting_response && (
                          <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-700">
                            <Clock className="h-3 w-3" />
                          </Badge>
                        )}
                        {channel.needs_followup && (
                          <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700">
                            <AlertTriangle className="h-3 w-3" />
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3 w-12 sticky right-0 z-10 bg-white shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.12)]" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 border border-slate-200">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => onSelectChannel?.(channel.id, channel.member_id)}>
                            <MessageSquare className="mr-2 h-4 w-4" /> Open Chat
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedChannels([channel.id]);
                            setAssignDialogOpen(true);
                          }}>
                            <UserPlus className="mr-2 h-4 w-4" /> Reassign
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <Activity className="mr-2 h-4 w-4" /> Change State
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="max-h-[300px] overflow-y-auto z-[200]">
                              {(Object.keys(ENGAGEMENT_STATE_CONFIG) as EngagementState[]).map((state) => {
                                const config = ENGAGEMENT_STATE_CONFIG[state];
                                const isCurrent = channel.engagement_state === state;
                                return (
                                  <DropdownMenuItem
                                    key={state}
                                    onSelect={() => {
                                      if (!isCurrent && !setStateMutation.isPending) {
                                        setStateMutation.mutate({ channelId: channel.id, state });
                                      }
                                    }}
                                    disabled={isCurrent || setStateMutation.isPending}
                                    className={cn(isCurrent && "bg-accent", "cursor-pointer")}
                                  >
                                    <span className={cn("mr-2", config.color)}>{config.icon}</span>
                                    {config.label}
                                    {isCurrent && <CheckCircle2 className="ml-auto h-4 w-4 text-green-500" />}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          {channel.engagement_state === 'opt_out' ? (
                            <DropdownMenuItem
                              onClick={() => reactivateMutation.mutate(channel.id)}
                            >
                              <Play className="mr-2 h-4 w-4" /> Reactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => optOutMutation.mutate(channel.id)}
                              className="text-destructive"
                            >
                              <Ban className="mr-2 h-4 w-4" /> Mark Opt-Out
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
                })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination / Count */}
      <div className="p-3 border-t text-sm text-muted-foreground">
        Showing {visibleCount} member conversation{visibleCount === 1 ? '' : 's'}
        {hideEmpty ? ' (empty hidden)' : ''}
      </div>

      {/* Case Manager Workload Sidebar (collapsible) */}
      {workloadData && workloadData.length > 0 && (
        <div className="border-t p-4">
          <h3 className="font-medium mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" /> Case Manager Workload
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {workloadData.slice(0, 6).map((cm) => (
              <Card key={cm.user_id} className="p-3">
                <div className="font-medium text-sm truncate">{cm.user_name}</div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Assigned: {cm.total_assigned}</span>
                  <span>Active: {cm.active_channels}</span>
                </div>
                {(cm.awaiting_response > 0 || cm.needs_followup > 0) && (
                  <div className="flex gap-2 mt-2">
                    {cm.awaiting_response > 0 && (
                      <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-700">
                        {cm.awaiting_response} awaiting
                      </Badge>
                    )}
                    {cm.needs_followup > 0 && (
                      <Badge variant="outline" className="text-xs bg-orange-100 text-orange-700">
                        {cm.needs_followup} follow-up
                      </Badge>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Reassign All Dialog */}
      <Dialog open={bulkReassignDialogOpen} onOpenChange={setBulkReassignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Reassign All Cases</DialogTitle>
            <DialogDescription>
              Transfer all member assignments and DM channels from one staff member to another.
              Use this when a case manager departs or for workload rebalancing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">From (current case manager)</label>
              <Select
                value={bulkFromUser?.toString()}
                onValueChange={(value) => setBulkFromUser(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member to reassign from" />
                </SelectTrigger>
                <SelectContent>
                  {workloadData && workloadData.length > 0 ? (
                    workloadData.map((cm: CaseManagerWorkload) => (
                      <SelectItem key={cm.user_id} value={cm.user_id.toString()}>
                        {cm.user_name} ({cm.total_assigned} assigned, {cm.active_channels} active)
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>
                      No staff members with assignments
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">To (new case manager)</label>
              <Select
                value={bulkToUser?.toString()}
                onValueChange={(value) => setBulkToUser(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select target case manager" />
                </SelectTrigger>
                <SelectContent>
                  {workloadData && workloadData.length > 0 ? (
                    workloadData
                      .filter((cm: CaseManagerWorkload) => cm.user_id !== bulkFromUser)
                      .map((cm: CaseManagerWorkload) => (
                        <SelectItem key={cm.user_id} value={cm.user_id.toString()}>
                          {cm.user_name} ({cm.total_assigned} assigned)
                        </SelectItem>
                      ))
                  ) : (
                    <SelectItem value="" disabled>
                      No staff members available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bulkIncludeEnrollments}
                  onCheckedChange={(checked) => setBulkIncludeEnrollments(!!checked)}
                />
                Enrollments
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bulkIncludeMembers}
                  onCheckedChange={(checked) => setBulkIncludeMembers(!!checked)}
                />
                Member assignments
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={bulkIncludeDM}
                  onCheckedChange={(checked) => setBulkIncludeDM(!!checked)}
                />
                DM channels
              </label>
            </div>
            <Input
              placeholder="Reason for reassignment (optional)"
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
            />
            {bulkReassignMutation.isSuccess && bulkReassignMutation.data && (
              <div className="p-3 bg-green-50 text-green-700 rounded text-sm">
                Reassigned {bulkReassignMutation.data.enrollments_reassigned || 0} enrollment(s),
                {' '}{bulkReassignMutation.data.member_assignments_reassigned} member assignment(s),
                and {bulkReassignMutation.data.dm_channels_reassigned} DM channel(s).
                {bulkReassignMutation.data.errors.length > 0 && (
                  <span className="text-orange-600"> ({bulkReassignMutation.data.errors.length} error(s))</span>
                )}
              </div>
            )}
            {bulkReassignMutation.isError && (
              <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                Reassignment failed. Please try again.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkReassignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkReassign}
              disabled={!bulkFromUser || !bulkToUser || bulkFromUser === bulkToUser || bulkReassignMutation.isPending || (!bulkIncludeDM && !bulkIncludeMembers && !bulkIncludeEnrollments)}
            >
              {bulkReassignMutation.isPending ? 'Reassigning...' : 'Reassign All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Channels</DialogTitle>
            <DialogDescription>
              Assign {selectedChannels.length} channel(s) to a case manager
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select
              value={selectedCaseManager?.toString()}
              onValueChange={(value) => setSelectedCaseManager(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select case manager" />
              </SelectTrigger>
              <SelectContent>
                {availableStaff && availableStaff.length > 0 ? (
                  availableStaff.map((cm) => (
                    <SelectItem key={cm.user_id} value={cm.user_id.toString()}>
                      {cm.user_name} ({cm.total_assigned} assigned)
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="" disabled>
                    No staff members available
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <Input
              placeholder="Reason for reassignment (optional)"
              value={assignReason}
              onChange={(e) => setAssignReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedCaseManager || assignMutation.isPending}
            >
              {assignMutation.isPending ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DMChannelManagement;
